import React, { useEffect, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'is24-corecss'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { AttachmentDataWithRequiredAttachment } from '../../../../../../../../../../@types/types'
import { PictureCard } from './PictureCard'
import { CarouselIndicators } from './CarouselIndicators'
import { CarouselActionButtons } from './CarouselActionButtons'
import styles from './PictureCarousel.module.css'

interface PictureCarouselProps {
  attachments: AttachmentDataWithRequiredAttachment[]
  selectedPictureId: string
  onSelect: (pictureId: string) => void
  onEditClick: (attachment: AttachmentDataWithRequiredAttachment) => void
  onDeleteClick?: (attachment: AttachmentDataWithRequiredAttachment) => void
  onAddClick: () => void
  dropzoneId: string
}

export const PictureCarousel: React.FC<PictureCarouselProps> = ({
  attachments,
  selectedPictureId,
  onSelect,
  onEditClick,
  onDeleteClick,
  onAddClick,
  dropzoneId,
}) => {
  const currentSlide = Math.max(
    0,
    attachments.findIndex((item) => item.attachment.id === selectedPictureId)
  )
  const swiperRef = useRef<SwiperType | null>(null)

  useEffect(() => {
    const swiper = swiperRef.current
    if (swiper && !swiper.destroyed && swiper.activeIndex !== currentSlide) {
      swiper.slideTo(currentSlide)
    }
  }, [currentSlide])

  if (attachments.length === 0) {
    return null
  }

  const currentAttachment = attachments[currentSlide]
  const totalPictures = attachments.length

  const selectSlide = (index: number) => {
    const picture = attachments[index]
    if (picture) {
      onSelect(picture.attachment.id)
    }
  }

  return (
    <div className={styles.carouselContainer}>
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        loop={false}
        initialSlide={currentSlide}
        onSwiper={(swiper) => {
          swiperRef.current = swiper
        }}
        onSlideChange={(swiper) => selectSlide(swiper.activeIndex)}
        className={styles.carouselSlider}
      >
        {attachments.map((attachment) => (
          <SwiperSlide key={attachment.attachment.id}>
            <PictureCard attachment={attachment} onClick={() => onEditClick(attachment)} />
          </SwiperSlide>
        ))}
      </Swiper>

      <CarouselIndicators
        currentSlide={currentSlide}
        totalPictures={totalPictures}
        currentAttachment={currentAttachment}
      />

      <CarouselActionButtons
        currentAttachment={currentAttachment}
        dropzoneId={dropzoneId}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        onAddClick={onAddClick}
      />

      {totalPictures > 1 && (
        <div className={styles.paginationContainer}>
          <Pagination
            type='dot'
            variant='on-image'
            pages={totalPictures}
            activePage={currentSlide}
            onPageChange={selectSlide}
          />
        </div>
      )}
    </div>
  )
}
